"use client";
import BookAuditCallButton from "../components/BookAuditCallButton";
import EmailPromptModal from "../components/EmailPromptModal";
import ReportModal from "../components/ReportModal";
import { useState } from "react";

export default function HomeHero() {
  // Report generation flow state
  const [isEmailPromptOpen, setIsEmailPromptOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // Email captured → launch the TopGEO report flow.
  const handleEmailSubmit = (email: string) => {
    setReportEmail(email);
    setEmailError("");
    setIsEmailPromptOpen(false);
    setIsReportModalOpen(true);
  };

  // ReportModal bubbled an email-related error (e.g. non-professional email).
  // Reopen the email prompt with the message.
  const handleReportError = (message: string) => {
    setIsReportModalOpen(false);
    setEmailError(message);
    setIsEmailPromptOpen(true);
  };

  return (
    <>
      <section className="w-full bg-surface py-12 sm:py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-14">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">

            {/* Left */}
            <div className="flex-1 w-full max-w-[600px]">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 border border-hairline text-ink-faint rounded-full font-mono text-[11px] sm:text-[12px] tracking-wide mb-6 sm:mb-8">
                AI SEARCH PIPELINE FOR B2B
              </div>

              {/* Headline */}
              <h1 className="font-display text-[36px] sm:text-5xl lg:text-6xl xl:text-[50px] font-black leading-[1.05] tracking-[-0.02em] mb-5 sm:mb-6" style={{ textWrap: 'balance' }}>
                <span className="text-ink">Your buyers are asking AI for a shortlist. </span>
                <span className="text-accent">Make sure your name is on it.</span>
              </h1>

              <p className="font-body text-[15px] sm:text-[17px] text-ink-muted leading-relaxed mb-8 sm:mb-10 max-w-[480px]">
                TopGeo publishes AI-optimized pages on your own domain, captures the enquiry/RFQ, and proves which AI engine sent it - real pipeline from AI search, not just a score.
              </p>

              <p className="font-body text-[15px] text-ink-faint font-medium mb-4 max-w-[480px]">
                TopGeo is the AI-search lead engine for mid-market B2B - we get you into AI answers and turn them into attributed enquiries on your own domain.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <BookAuditCallButton
                  label="Book an Audit Call"
                  className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3.5 bg-accent text-on-accent font-semibold text-[15px] rounded-lg active:scale-[0.97] transition-transform duration-[160ms] [transition-timing-function:var(--ease-out)] hover:opacity-90 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                />
                {/* Product tour hidden for now — re-enable when ready.
                <a href="/product-tour" target="_blank" rel="noopener noreferrer" aria-label="Watch Product Tour (opens in new tab)" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-hairline text-ink font-semibold text-[15px] rounded-lg active:scale-[0.97] transition-transform duration-[160ms] [transition-timing-function:var(--ease-out)] hover:bg-surface-raised cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2">
                  <span className="w-5 h-5 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
                    <svg width="8" height="9" viewBox="0 0 8 9" fill="white" aria-hidden="true"><path d="M1 1.5l6 3-6 3V1.5z"/></svg>
                  </span>
                  Watch Product Tour
                </a>
                */}
              </div>
            </div>

            {/* Right - replaces fake metric card; static mockup shows the pain in one glance */}
            <div className="flex-1 relative max-w-[560px] w-full mt-10 lg:mt-0">
              {/* animate-float removed - decision: no animation on hero widget */}
              <div className="absolute -top-5 right-2 sm:right-4 bg-surface-raised shadow-lg rounded-xl px-3 sm:px-4 py-2.5 flex items-center gap-2.5 z-10 border border-hairline">
                <div className="w-7 h-7 rounded-lg bg-[#10A37F] flex items-center justify-center">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.51-2.9A5.98 5.98 0 0 0 4.98 4 6.05 6.05 0 0 0-.77 8.19 5.98 5.98 0 0 0 1 13.1a6.05 6.05 0 0 0 .74 7.1 5.98 5.98 0 0 0 6.51 2.9A5.98 5.98 0 0 0 13.26 24a6.06 6.06 0 0 0 5.77-4.21 5.99 5.99 0 0 0 3.98-2.9 6.06 6.06 0 0 0-.75-7.07z"/></svg>
                </div>
                <span className="text-[13px] font-semibold text-ink">ChatGPT - AI Answer</span>
              </div>

              {/* Main card - browser-style AI answer mockup */}
              <div className="bg-surface-raised rounded-2xl shadow-xl border border-hairline overflow-hidden">
                {/* Browser chrome */}
                <div className="bg-surface border-b border-hairline px-4 py-2.5 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                    <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                    <div className="w-3 h-3 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="flex-1 bg-surface-raised border border-hairline rounded-md px-3 py-1 text-[11px] text-ink-faint mx-2 truncate">
                    chat.openai.com
                  </div>
                </div>

                {/* Chat content */}
                <div className="p-5 flex flex-col gap-4">
                  {/* User query bubble */}
                  <div className="flex justify-end">
                    <div className="bg-surface rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%] border border-hairline">
                      <p className="text-[13px] text-ink">best CNC parts supplier for export?</p>
                    </div>
                  </div>

                  {/* AI response */}
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#10A37F] flex-shrink-0 flex items-center justify-center mt-0.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.51-2.9A5.98 5.98 0 0 0 4.98 4 6.05 6.05 0 0 0-.77 8.19 5.98 5.98 0 0 0 1 13.1a6.05 6.05 0 0 0 .74 7.1 5.98 5.98 0 0 0 6.51 2.9A5.98 5.98 0 0 0 13.26 24a6.06 6.06 0 0 0 5.77-4.21 5.99 5.99 0 0 0 3.98-2.9 6.06 6.06 0 0 0-.75-7.07z"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-ink-muted leading-relaxed mb-3">
                        Based on export certifications and buyer reviews, here are the most recommended CNC suppliers:
                      </p>
                      <div className="flex flex-col gap-2">
                        {/* Fictional company names — intentional, not real customers */}
                        {[
                          { rank: "1", name: "Acme Precision Parts", tag: "ISO 9001 - ships to 40+ countries" },
                          { rank: "2", name: "GlobalMachined Inc.", tag: "Automotive & aerospace specialist" },
                          { rank: "3", name: "PrecisionEdge Corp", tag: "Fast lead times - EU-compliant" },
                        ].map(item => (
                          <div key={item.rank} className="flex items-center gap-2.5 bg-surface rounded-lg px-3 py-2 border border-hairline">
                            <span className="text-[11px] font-bold text-ink-faint w-4 flex-shrink-0">{item.rank}.</span>
                            <div>
                              <p className="text-[12px] font-semibold text-accent leading-tight">{item.name}</p>
                              <p className="text-[10px] text-ink-faint mt-0.5">{item.tag}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Missing brand indicator */}
                  <div className="border border-dashed border-signal-negative bg-signal-negative/10 rounded-xl px-4 py-3 flex items-center gap-2.5">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-signal-negative" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span className="text-[12px] text-signal-negative font-medium">Your brand is not cited in this response</span>
                  </div>
                </div>

                <p className="text-[10px] text-ink-faint text-center pb-3 -mt-1">
                  Illustrative example - not a real ChatGPT response
                </p>
              </div>

              {/* Bottom-left mini card - static, no animation */}
              <div className="absolute -bottom-4 left-2 sm:-left-4 bg-surface-raised shadow-md rounded-xl px-3 py-2 flex items-center gap-2 border border-hairline">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent" aria-hidden="true"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                <span className="text-[12px] font-semibold text-ink">Live Tracking</span>
              </div>
            </div>

          </div>
        </div>
      </section>
      {/* AI Visibility Report flow: email gate → report modal (TopGEO backend) */}
      <EmailPromptModal
        isOpen={isEmailPromptOpen}
        onClose={() => setIsEmailPromptOpen(false)}
        onSubmit={handleEmailSubmit}
        initialError={emailError}
      />
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        userEmail={reportEmail}
        onError={handleReportError}
      />
    </>
  );
}
